import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, SparkleIcon, SyncIcon} from '@primer/octicons-react'
import {IconButton, SkeletonBox} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useEffectEvent} from 'react'

import styles from './AllowedValuesSuggestion.module.css'

export interface AllowedValuesSuggestionProps {
  suggestedValues: string[]
  onAccept: () => void
  onRefresh: () => void
  isRefreshing: boolean
  className?: string
}

export function AllowedValuesSuggestion({
  suggestedValues,
  onAccept,
  onRefresh,
  isRefreshing,
  className,
}: AllowedValuesSuggestionProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const noSuggestions = suggestedValues.length === 0

  const announceSuggestion = useEffectEvent(() => {
    if (noSuggestions || isRefreshing) return
    announce(`${suggestedValues.length} suggested ${suggestedValues.length === 1 ? 'option' : 'options'}.`)
  })

  const sendShownAnalyticsEvent = useEffectEvent(() => {
    if (noSuggestions) return
    sendAnalyticsEvent('repo_custom_properties_suggestions.allowed_values.shown')
  })

  useEffect(() => {
    announceSuggestion()
    sendShownAnalyticsEvent()
  }, [isRefreshing])

  if (isRefreshing) {
    return <AllowedValuesSkeleton className={className} />
  }

  if (noSuggestions) {
    return null
  }

  return (
    <div
      data-testid="allowed-values-suggestion"
      className={clsx('d-flex flex-items-center gap-3 pl-3 pr-2 py-1 color-bg-subtle border-top', className)}
    >
      <SparkleIcon size="small" className="color-fg-muted ml-1" />

      <PreviewValues suggestedValues={suggestedValues} />

      <div className={clsx('d-flex flex-items-center ml-auto', styles.SmallMarginRight)}>
        <div className="d-flex gap-1">
          <IconButton
            size="small"
            variant="invisible"
            icon={CheckIcon}
            aria-label="Accept suggestion"
            onClick={() => {
              sendAnalyticsEvent('repo_custom_properties_suggestions.allowed_values.clicked')
              onAccept()
            }}
          />
          <IconButton
            size="small"
            variant="invisible"
            icon={SyncIcon}
            aria-label="Refresh suggestion"
            onClick={onRefresh}
          />
        </div>
      </div>
    </div>
  )
}

function AllowedValuesSkeleton({className}: {className?: string}) {
  return (
    <div
      data-testid="allowed-values-suggestion-skeleton"
      className={clsx('d-flex flex-items-center gap-3 p-1 tmp-px-3 color-bg-subtle border-top', className)}
    >
      <SkeletonBox width={24} height={24} className="rounded-2" />

      <div className="d-flex gap-2">
        <SkeletonBox width={70} height={20} className="rounded-2" />
        <SkeletonBox width={50} height={20} className="rounded-2" />
      </div>

      <div className="d-flex flex-items-center gap-2 ml-auto">
        <div className="d-flex gap-1">
          <SkeletonBox width={28} height={28} className="rounded-2" />
          <SkeletonBox width={28} height={28} className="rounded-2" />
        </div>
      </div>
    </div>
  )
}

function PreviewValues({suggestedValues}: {suggestedValues: string[]}) {
  const [firstValue, secondValue] = suggestedValues
  const totalValues = suggestedValues.length

  return (
    <>
      {/* Display two preview values on wider viewports */}
      <div className={clsx('flex-items-center gap-2 ml-1 min-width-0', styles.HideOnSmallScreen)}>
        <strong className={styles.Truncate}>
          {firstValue}
          {secondValue && <span>, {secondValue}</span>}
        </strong>
        {totalValues > 2 && <span className="color-fg-muted no-wrap"> + {totalValues - 2} more</span>}
      </div>

      {/* Display one preview value on narrow viewports */}
      <div className={clsx('flex-items-center gap-2 ml-1 min-width-0', styles.SmallScreenOnly)}>
        <strong className={styles.Truncate}>{firstValue}</strong>
        {totalValues > 1 && <span className="color-fg-muted no-wrap"> + {totalValues - 1} more</span>}
      </div>
    </>
  )
}
